import { useState } from 'react'
import { UsersViewType, UsersSortKeys } from './types'
import { TableColumn, DataTable } from '@/components/data-table'
import { Badge } from '@/components/ui/badge'
import dayjs from 'dayjs'
import { ChangeUserRole, RemoveUserDialog } from './UserActions'
import Gravatar from 'react-gravatar'
import {
  getOrgUsers,
  useGetOrgUsers,
  useCreateOrgInviteBatch,
  useResendOrgInvite,
  LIST_USERS_PAGE_SIZE,
} from '@/services/user-management'
import { RoleID, UserOrganization, WebrpcError } from '@/services/inference.gen'
import { toast } from 'react-hot-toast'
import { numToRoleID, hasAdminPrivileges } from '@/services/utils'
import { Button } from '@/components/ui/button'
import { UserManagementHeader } from './Header'
import { RefreshCw, Info, Shield, User } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export const DashboardUserManagement = () => {
  const [usersView, onUsersViewChange] = useState(UsersViewType.EXISTING)
  const [searchTerm, setSearchTerm] = useState('')
  const [userSort, setUserSort] = useState(UsersSortKeys.JOINED_AT_DESC)
  const [resendingUserId, setResendingUserId] = useState<number | null>(null)

  const status = usersView === UsersViewType.EXISTING ? 'active' : 'invited'

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetOrgUsers(status, searchTerm, userSort)

  const { mutateAsync: createInviteBatch, isPending: isResending } =
    useCreateOrgInviteBatch()

  const { mutateAsync: resendInvite, isPending: isResendingSingle } =
    useResendOrgInvite()

  const allUsers = data?.allUsers || []
  const total = data?.total || 0

  const handleResendInvite = async (
    email: string,
    role: RoleID,
    userId: number
  ) => {
    setResendingUserId(userId)
    try {
      await resendInvite({ email, role })
    } catch (error) {
      console.error('Error resending invite:', error)
      if (error instanceof WebrpcError) {
        toast.error(JSON.stringify(error))
      } else {
        toast.error('Failed to resend invite')
      }
    } finally {
      setResendingUserId(null)
    }
  }

  const handleBulkResend = async () => {
    if (isResending) return

    try {
      // Fetch all pending users dynamically
      const fetchAllPendingUsers = async () => {
        const results = []
        let currentPage = 1
        let totalFetched = 0
        const totalUsers = total

        while (totalFetched < totalUsers) {
          const { users } = await getOrgUsers({
            page_number: currentPage,
            page_size: LIST_USERS_PAGE_SIZE,
            status: 'invited',
            sort_by: 'created_at',
            sort_direction: 'desc',
            search_text: '',
          })

          results.push(...users)
          totalFetched += users.length
          currentPage++
        }

        return results
      }

      const allPendingUsers = await fetchAllPendingUsers()

      // Map to invites
      const invites = allPendingUsers.map((user) => ({
        email: user.user?.email || '',
        role: numToRoleID(user.role?.role_id),
      }))

      if (!invites.length) {
        toast.error('No pending users to resend invites to.')
        return
      }

      // Send batch invites
      await createInviteBatch(invites)

      toast.success('Re-invites sent successfully to all pending users.')
    } catch (error) {
      console.error('Error resending invites:', error)
      if (error instanceof WebrpcError) {
        toast.error(JSON.stringify(error))
      } else {
        toast.error('Failed to resend invites. Please try again.')
      }
    }
  }

  const columns: TableColumn<UserOrganization>[] = [
    {
      id: 'user',
      header: 'User',
      width: 'w-[40%]',
      cell: (user) => {
        const fullName =
          [
            user.profile_metadata?.first_name || '',
            user.profile_metadata?.last_name || '',
          ]
            .filter(Boolean)
            .join(' ') || '--'

        return (
          <div className="flex items-center space-x-3">
            {user.profile_metadata?.avatar_preview_url ? (
              <img
                src={user.profile_metadata.avatar_preview_url}
                alt={`${fullName}'s avatar`}
                className="h-10 w-10 rounded-full object-cover border border-border"
              />
            ) : (
              <Gravatar
                email={user.user?.email || ''}
                className="h-10 w-10 rounded-full border border-border"
              />
            )}
            <div className="space-y-1">
              <div className="font-medium">{fullName}</div>
              <div className="text-sm text-muted-foreground">
                {user.user?.email}
              </div>
            </div>
          </div>
        )
      },
    },
    {
      id: 'role',
      header: 'Role',
      width: 'w-[20%]',
      cell: (user) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center space-x-1.5">
                {hasAdminPrivileges(user.role?.role_name) ? (
                  <Shield className="h-4 w-4 text-primary" />
                ) : (
                  <User className="h-4 w-4 text-muted-foreground" />
                )}
                <span
                  className={
                    hasAdminPrivileges(user.role?.role_name)
                      ? 'font-medium text-primary'
                      : ''
                  }
                >
                  {user.role?.role_name}
                </span>
                {user.user?.is_super_admin && (
                  <Badge variant="outline" className="ml-1 text-xs py-0 px-1.5">
                    Super
                  </Badge>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {hasAdminPrivileges(user.role?.role_name)
                  ? 'Administrator with full access'
                  : 'Regular user with limited access'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      id: 'status',
      header:
        usersView === UsersViewType.PENDING
          ? 'Invitation Status'
          : 'Joined Date',
      width: 'w-[25%]',
      cell: (user) => {
        if (usersView === UsersViewType.PENDING) {
          const isExpired =
            !user.invitation?.expires_at ||
            dayjs().isAfter(dayjs(user.invitation?.expires_at))
          const timeLeft = user.invitation?.expires_at
            ? dayjs(user.invitation?.expires_at).diff(dayjs(), 'days')
            : 0

          return isExpired ? (
            <Badge variant="destructive" className="font-normal">
              Expired
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="bg-primary/10 text-primary border-primary/30 font-normal"
            >
              Expires in {timeLeft} {timeLeft === 1 ? 'day' : 'days'}
            </Badge>
          )
        } else {
          return (
            <div className="text-sm text-muted-foreground">
              {dayjs(user.joined_at).format('MMMM DD, YYYY')}
            </div>
          )
        }
      },
    },
    {
      id: 'actions',
      header: <div className="text-right">Actions</div>,
      width: 'w-[15%]',
      cell: (user) => (
        <div className="flex items-center justify-end space-x-2">
          {usersView === UsersViewType.PENDING && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 px-2 flex items-center gap-1"
              onClick={() =>
                handleResendInvite(
                  user.user?.email || '',
                  numToRoleID(user.role?.role_id),
                  user.user_id
                )
              }
              disabled={isResendingSingle && resendingUserId === user.user_id}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              <span>
                {isResendingSingle && resendingUserId === user.user_id
                  ? 'Sending...'
                  : 'Resend'}
              </span>
            </Button>
          )}

          <div className="flex items-center space-x-1">
            <ChangeUserRole
              disabled={usersView === UsersViewType.PENDING}
              userId={user.user_id.toString()}
              userEmail={user.user?.email || ''}
              role={numToRoleID(user.role?.role_id)}
            />

            <RemoveUserDialog
              userId={user.user_id.toString()}
              userEmail={user.user?.email || ''}
              usersViewType={usersView}
            />
          </div>
        </div>
      ),
    },
  ]

  const headerInfo = !isLoading ? (
    <div
      className="flex items-center justify-between w-full"
      data-testid="users-header-info"
    >
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Info className="h-4 w-4" />
        <span data-testid="users-count-info">
          Showing {allUsers.length} of {total}{' '}
          {status === 'active' ? 'active' : 'invited'} users
        </span>
      </div>

      {usersView === UsersViewType.PENDING && total > 0 && (
        <Button
          size="sm"
          variant="outline"
          onClick={handleBulkResend}
          disabled={isLoading || isResending}
          className="gap-1.5"
          data-testid="bulk-resend-button"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {isResending ? 'Re-sending...' : 'Re-Invite All Pending'}
        </Button>
      )}
    </div>
  ) : null

  const originalHeader = (
    <div className="space-y-6 mb-4" data-testid="user-management-header">
      <UserManagementHeader
        usersViewType={usersView}
        onUsersViewChange={onUsersViewChange}
        onSearchChange={setSearchTerm}
        usersViewSortType={userSort}
        onUsersViewSortChange={setUserSort}
      />

      {headerInfo && (
        <div className="flex items-center justify-between px-1 py-2">
          {headerInfo}
        </div>
      )}
    </div>
  )

  return (
    <DataTable
      customHeader={originalHeader}
      data={allUsers}
      columns={columns}
      keyExtractor={(user) => user.user_id}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      emptyStateMessage={`No ${status} users found`}
      loadingMessage="Loading users..."
      data-testid="users-data-table"
    />
  )
}
